import React, {useContext, useEffect, useState} from "react";
import MainStyle from "../../MainStyle";
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import ServiceClient from "../utils/ServiceClient";
import {AxiosError} from "axios";
import {SavedCardType} from "../types";
import {StoreContext} from "./StoreContextProvider";
import {useNavigation} from "@react-navigation/native";
import SavedCardsModal from "../modals/SavedCardsModal";
import Div from "../lib/Div";
import P from "../lib/P";
import Btn from "./Btn";
import {getLocation} from "../utils";
import DeleteCardModal from "../modals/DeleteCardModal";

type SelectedCardDeposit = { card: SavedCardType, amountCents: number };

type Props = { amountCents: number | null, onInvalidAmount: () => void };

const SavedCards = (props: Props) => {
  const {amountCents, onInvalidAmount} = props;
  const [savedCards, setSavedCards] = useState<SavedCardType[]>([]);
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user} = Context;
  const navigation: any = useNavigation();
  const [selectedDeposit, setSelectedDeposit] = useState<SelectedCardDeposit>();
  const [removeCard, setRemoveCard] = useState<SavedCardType>();

  const deposit = async (cardUid: string, expiry: string) => {
    const submittedAmountCents = selectedDeposit?.amountCents;
    setSelectedDeposit(undefined);

    if (submittedAmountCents === undefined) return;

    Context.setLoadingOverlay(true);
    const curLocation = await getLocation();

    if (!curLocation) {
      Context.setLoadingOverlay(false);
      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: 'Permission to access the location was denied.', rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });

      return;
    }

    const data = {
      amount: submittedAmountCents, savedCardUid: cardUid, expirationDate: expiry,
      location: {latitude: `${curLocation.coords.latitude}`, longitude: `${curLocation.coords.longitude}`}
    };

    ServiceClient.deposit(data).then(() => {
      Context.getUser().then();
      Context.setLoadingOverlay(false);

      Context.setCancelModalConfig({
        visible: true, title: 'Success', titleColor: MainStyle.c_green, leftBtnText: 'OK',
        subtitle: 'You have successfully replenished your balance.',
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.navigate('challenges');
        }
      });

    }).catch((err: AxiosError<{ success: boolean, message: string }>) => {
      console.log('deposit error', err);
      Context.setLoadingOverlay(false);
      const subtitle = (err.response && err.response.data) ? err.response.data.message : err.message;

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle, rightBtnText: 'OK',
        onResume: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      });
    });
  };

  const deleteCard = (Uid?: string) => {
    setRemoveCard(undefined);
    if (!Uid) return;

    Context.setCancelModalConfig({...cancelModalConfig, visible: false});
    Context.setLoadingOverlay(true);

    ServiceClient.deleteCard(Uid).then(() => {
      getSavedCards();

    }).catch(err => {
      console.log('deleteCard err', err);
      Context.setLoadingOverlay(false);
      const subtitle = (err.response && err.response.data) ? err.response.data.message : err.message;

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const savedCardHandler = (card: SavedCardType) => () => {
    if (user?.selfLimitationConfirmed) {
      Context.setCancelModalConfig({
        visible: true, title: 'Notice', titleColor: MainStyle.c_yellow, leftBtnText: 'OK',
        subtitle: 'You will not be able to play or deposit money while the Self-Limitation is in effect.',
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.navigate('SelfExclusion');
        }
      });

    } else if (amountCents === null) {
      onInvalidAmount();

    } else {
      setSelectedDeposit({card, amountCents});
    }
  };

  const getToken = () => {
    ServiceClient.getPayGateToken().then(() => {
      getSavedCards();

    }).catch(err => {
      console.log('getPayGateToken err', err);
      Context.setLoadingOverlay(false);
      const subtitle = (err.response && err.response.data) ? err.response.data.message ? err.response.data.message : err.response.data.details ? err.response.data.details : 'GetPayGateToken Error' : err.message;

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle, rightBtnText: 'OK',
        onResume: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      });
    });
  };

  const getSavedCards = () => {
    ServiceClient.getSavedCards().then(res => {
      setSavedCards(res);
      Context.setLoadingOverlay(false);

    }).catch((err: AxiosError<{ success: boolean, message: string }>) => {
      console.log('getSavedCards error', err);
    });
  };

  useEffect(() => {
    getToken();
  }, []);

  return (
    <>
      {!!savedCards.length && (
        <>
          <P fontFamily="DavisSans-Regular" mb={8} color="#BBBCBF">Payment Method</P>

          <Div gap={8}>
            {savedCards.map(el => (
              <Div row h={64} key={el.cardUid} rounded={12} bg="#252526">
                <Btn flex={1} justifyContent="center" onPress={savedCardHandler(el)} px={16}>
                  <P fontFamily="DavisSans-Regular">{el.cardMaskedNumber.slice(-8)}</P>
                </Btn>

                <Btn justifyContent="center" px={13} onPress={() => setRemoveCard(el)}>
                  <Div w={28} h={28} bg="#494A4C" alignItems="center" justifyContent="center" rounded={15}>
                    <FontAwesome5 name="trash-alt" size={16} color="#F56E6E"/>
                  </Div>
                </Btn>
              </Div>
            ))}
          </Div>
        </>
      )}

      <SavedCardsModal card={selectedDeposit?.card} onClose={() => setSelectedDeposit(undefined)} onConfirm={deposit}/>
      <DeleteCardModal card={removeCard} onClose={deleteCard}/>
    </>
  );
};

export default React.memo(SavedCards);
